import type { Action } from './actions'
import endpoints from 'shared/store/endpoints'
import { managePageAPIError } from 'shared/store/utils'
import type {
    CreditCardInfo,
    Kid,
    MetaShortDTO,
    OptinDTO,
    RestaurantShortDTO,
    StoredPaymentMethod
} from 'shared/types/api'

export type State = {
    metaData: ?MetaShortDTO,
    blazonProfileImageUrl?: string,
    email: string,
    civility: string,
    lastname: string,
    firstname: string,
    phone: string,
    carColor: string,
    licensePlateNumber: string,
    licensePlateEnrolment: boolean,
    birthdate: string,
    maxKids: ?number,
    emailPrivacy: string,
    kids: Kid[],
    creditCards: CreditCardInfo[],
    creditCardsV2: StoredPaymentMethod[],
    favRestaurant?: RestaurantShortDTO,
    restaurants: RestaurantShortDTO[],
    selectedRestaurant: ?RestaurantShortDTO,
    addRestaurantSuccess?: boolean,
    addRestaurantError: ?string,
    optins: ?OptinDTO,
    profileUpdateSuccess?: boolean,
    profileUpdateError: ?string,
    updateEmailSuccess?: boolean,
    updateEmailError: ?string,
    activateEmailSuccess?: boolean,
    activateEmailError: ?string,
    updatePasswordSuccess?: boolean,
    updatePasswordError: ?string,
    addKidSuccess?: boolean,
    addKidError: ?string,
    dataLoaded?: boolean,
    optinsLoaded?: boolean,
    profileError?: any,
    profileNetworkError?: any,
    hasResetPassword?: boolean,
    deleteKidError: boolean,
    deleteProfileError: boolean
}

const initialState: State = {
    metaData: null,
    blazonProfileImageUrl: '',
    email: '',
    civility: '',
    lastname: '',
    firstname: '',
    phone: '',
    carColor: '',
    licensePlateNumber: '',
    licensePlateEnrolment: false,
    birthdate: '',
    maxKids: null,
    emailPrivacy: '',
    creditCards: [],
    creditCardsV2: [],
    kids: [],
    favRestaurant: undefined,
    restaurants: [],
    selectedRestaurant: null,
    addRestaurantError: null,
    optins: null,
    profileUpdateError: undefined,
    updateEmailError: undefined,
    activateEmailError: undefined,
    updatePasswordError: undefined,
    kid: null,
    addKidError: undefined,
    deleteKidError: false,
    deleteProfileError: false
}

export const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case 'PAGE_DATA_PROFILE_RESPONSE':
            const { analyticsData, ...profileState } = action.data
            return {
                ...state,
                ...profileState,
                dataLoaded: true,
                profileError: undefined,
                profileNetworkError: undefined
            }
        case 'PROFILE_OPTINS_RESPONSE':
            return {
                ...state,
                optinsLoaded: true,
                optins: action.data
            }
        case 'PROFILE_UPDATE_SUCCESS':
            return {
                ...state,
                profileUpdateSuccess: true,
                profileUpdateError: null,
                ...action.profile
            }
        case 'PROFILE_UPDATE_ERROR':
            return {
                ...state,
                profileUpdateSuccess: undefined,
                profileUpdateError: action.profileUpdateError
            }
        case 'PROFILE_RESET_UPDATE_SUCCESS':
            return {
                ...state,
                profileUpdateSuccess: undefined,
                profileUpdateError: null
            }
        case 'PROFILE_DELETE_CREDIT_CARD_SUCCESS':
            return {
                ...state,
                creditCards: action.creditCards
            }
        case 'PROFILE_DELETE_CREDIT_CARD_V2_SUCCESS':
            return {
                ...state,
                creditCardsV2: action.creditCardsV2
            }
        case 'PROFILE_EMAIL_UPDATE_SUCCESS':
            return {
                ...state,
                updateEmailSuccess: true,
                updateEmailError: null
            }
        case 'PROFILE_EMAIL_UPDATE_ERROR':
            return {
                ...state,
                updateEmailSuccess: undefined,
                updateEmailError: action.updateEmailError
            }
        case 'PROFILE_RESET_EMAIL_UPDATE_SUCCESS':
            return {
                ...state,
                updateEmailSuccess: undefined,
                updateEmailError: null
            }
        case 'PROFILE_EMAIL_ACTIVATE_SUCCESS':
            return {
                ...state,
                email: action.email,
                activateEmailSuccess: true,
                activateEmailError: null
            }
        case 'PROFILE_EMAIL_ACTIVATE_ERROR':
            return {
                ...state,
                activateEmailSuccess: undefined,
                activateEmailError: action.activateEmailError
            }
        case 'EMAIL_ACTIVATION_PATH_ERROR':
            return {
                ...state,
                activateEmailSuccess: undefined,
                activateEmailError: 'form.error.default'
            }
        case 'PROFILE_RESET_EMAIL_ACTIVATE_SUCCESS':
            return {
                ...state,
                activateEmailSuccess: undefined,
                activateEmailError: null
            }
        case 'PROFILE_PASSWORD_UPDATE_SUCCESS':
            return {
                ...state,
                updatePasswordSuccess: true,
                updatePasswordError: null
            }
        case 'PROFILE_PASSWORD_UPDATE_ERROR':
            return {
                ...state,
                updatePasswordSuccess: undefined,
                updatePasswordError: action.updatePasswordError
            }
        case 'PROFILE_RESET_PASSWORD_UPDATE_SUCCESS':
            return {
                ...state,
                updatePasswordSuccess: undefined,
                updatePasswordError: null
            }
        case 'PROFILE_ADD_KID_SUCCESS':
            return {
                ...state,
                kids: action.kids,
                addKidSuccess: true,
                addKidError: null
            }
        case 'PROFILE_ADD_KID_ERROR':
            return {
                ...state,
                addKidSuccess: undefined,
                addKidError: action.addKidError
            }
        case 'PROFILE_RESET_ADD_KID_SUCCESS':
            return {
                ...state,
                addKidSuccess: undefined,
                addKidError: null
            }
        case 'PROFILE_DELETE_KID_SUCCESS':
            return {
                ...state,
                kids: action.kids
            }
        case 'PROFILE_DELETE_KID_ERROR':
            return {
                ...state,
                deleteKidError: true
            }
        case 'PROFILE_RESET_DELETE_KID_ERROR':
            return {
                ...state,
                deleteKidError: false
            }
        case 'SEARCH_RESULTS':
            return {
                ...state,
                restaurants: action.restaurants
            }
        case 'CLEAR_SEARCH':
            return {
                ...state,
                restaurants: [],
                selectedRestaurant: null
            }
        case 'PROFILE_SELECT_RESTAURANT':
            return {
                ...state,
                selectedRestaurant: action.selectedRestaurant
            }
        case 'PROFILE_ADD_RESTAURANT_SUCCESS':
            return {
                ...state,
                favRestaurant: action.favRestaurant,
                addRestaurantSuccess: true,
                addRestaurantError: null
            }
        case 'PROFILE_FETCH_RESTAURANT_SUCCESS':
            return {
                ...state,
                favRestaurant: action.favRestaurant
            }
        case 'PROFILE_ADD_RESTAURANT_ERROR':
            return {
                ...state,
                addRestaurantSuccess: undefined,
                addRestaurantError: action.addRestaurantError
            }
        case 'PROFILE_RESET_ADD_RESTAURANT_SUCCESS':
            return {
                ...state,
                selectedRestaurant: null,
                restaurants: [],
                addRestaurantSuccess: undefined,
                addRestaurantError: null
            }
        /*case 'PROFILE_DELETE_RESTAURANT_SUCCESS':
            return {
                ...state,
                favRestaurants: action.favRestaurants
            }*/
        case 'SIGNIN_SEND_FORM_SUCCESS':
        case 'SOCIAL_PENDING':
        case 'SIGNUP_SEND_FORM_SUCCESS':
            return {
                ...state,
                profileError: undefined
            }
        case 'PROFILE_DELETE_ERROR':
            return {
                ...state,
                deleteProfileError: true
            }
        case 'PROFILE_RESET_UPDATE_ERROR':
            return {
                ...state,
                profileUpdateError: undefined
            }
        case 'PROFILE_RESET_DELETE_ERROR':
            return {
                ...state,
                deleteProfileError: false
            }
        case 'LOGOUT':
            return initialState
        case 'PAGE_API_ERROR':
            return managePageAPIError(
                state,
                action,
                endpoints.kingdom.profile.user,
                'profileError'
            )
        case 'PAGE_API_AUTH_ERROR':
            return {
                ...state,
                profileError: action.error
            }
        case 'RESET_NETWORK_ERROR':
            return {
                ...state,
                profileNetworkError: undefined
            }
        case 'PAGE_NETWORK_ERROR':
            return {
                ...state,
                profileNetworkError: action.networkError,
                profileError: undefined
            }
        case 'UPDATE_CAR_DETAILS':
            return {
                ...state,
                carColor: action.carColor,
                licensePlateNumber: action.licensePlateNumber,
                licensePlateEnrolment: action.licensePlateEnrolment
            }

        case 'UPDATE_OPTINS':
            return {
                ...state,
                optins: action.optins
            }
        default:
            return state
    }
}
